import { HTMLAttributes, FC, useMemo, useEffect } from "react";
import { NavigationBar } from "../NavigationBar/NavigationBar";
import { FooterControl } from "../FooterControl/FooterControl";
import { useAppSelector } from "../../hooks";
import { useStyles } from "./MainLayout.style";
import { useLocation, useNavigate, ScrollRestoration } from "react-router-dom";
import { selectIsAuthenticated } from "@onmo/auth";
import { useQuery } from "@apollo/client";
import { meAAAGQL, Query, UserSubscriptionStatus } from "@onmo/graphql/graphql";
import { LoadingScreen } from "../LoadingScreen/LoadingScreen";
import SetProfile from "../../../pages/SetProfile/SetProfile";
import { isShowPaywall } from "../../../models/storage";
import { PageRoutes } from "../../../constants/routes";
import { AnalyticsGA4 } from "../../../models/analyticsGA4";
import { isSubscriptionDisabled, isTV } from "../../../utils/platform";
import { BottomTabBar } from "../BottomTabBar/BottomTabBar";
import { MobileHeader } from "../MobileHeader/MobileHeader";
import { getPageByPath } from "../../../models/navigation";
import { GameDetailHeader } from "../NavigationBar/GameDetailHeader";
import clsx from "classnames";
import { selectIsDesktopView } from "../../../store/slices/appState";
import { usePingEonCluster } from "../../../hooks/usePingEonCluster";
import {
  APP_READY_EVENT,
  isSplashScreenEnabled,
} from "../SplashScreen/SplashScreen";
import { useAAASubscription } from "../../../hooks/useAAASubscription";

type MainLayoutProps = HTMLAttributes<HTMLDivElement>;
const USER_ID = "user_id";

export const MainLayout: FC<MainLayoutProps> = (props) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isDesktop = useAppSelector(selectIsDesktopView);
  const isGameDetailPage = location.pathname.startsWith(PageRoutes.Game);
  const isMobileGameDetail = !isDesktop && isGameDetailPage;
  const { data: dataMe } = useQuery<Query>(meAAAGQL, {
    skip: !isAuthenticated,
    onCompleted: (data) => sessionStorage.setItem(USER_ID, data?.me?.id),
  });
  const { data: dataSubscription } = useAAASubscription({
    skip: !isAuthenticated,
  });

  const showSetProfile = useMemo(() => {
    if (!isAuthenticated || !dataMe || isTV) return false;
    if (
      dataSubscription?.me?.subscription?.status !==
      UserSubscriptionStatus.Subscribed
    )
      return false;

    const onboarding = JSON.parse(dataMe?.me?.metadata?.onboarding || "{}");
    return !onboarding?.hasCheckedUserNameAvatar;
  }, [isAuthenticated, dataMe, dataSubscription]);

  const renderHeader = () => {
    if (showSetProfile) return null;
    if (isDesktop) {
      if (location?.state?.isFromGameTab) {
        return <GameDetailHeader />;
      } else {
        return <NavigationBar />;
      }
    } else {
      return (
        <MobileHeader
          type="main"
          name={getPageByPath(location.pathname)}
          className={clsx(isMobileGameDetail && classes.hide)}
        />
      );
    }
  };

  useEffect(() => {
    // Never push an unsubscribed user into the paywall on builds without the
    // subscription flow — they stay on the app and get the "Subscription
    // Required" popup only when they act on a paid feature.
    if (!isAuthenticated || !dataSubscription || isSubscriptionDisabled) return;

    if (isShowPaywall(dataSubscription?.me?.subscription?.status)) {
      navigate(PageRoutes.Paywall, { state: { fromTo: "onboarding" } });
    }
  }, [isAuthenticated, navigate, dataSubscription]);

  // Send event to gg analytics
  useEffect(() => {
    if (!isAuthenticated || !dataSubscription || !dataMe?.me?.id) return;

    const subscriptionStatus = () => {
      switch (dataSubscription?.me?.subscription?.status) {
        case UserSubscriptionStatus.Subscribed:
          return "active";
        case UserSubscriptionStatus.Expired:
          return "expired";
        default:
          return "inactive";
      }
    };
    AnalyticsGA4.subscriptionStatus(
      dataMe?.me?.id,
      subscriptionStatus(),
      dataSubscription?.me?.subscription?.status,
    );
  }, [isAuthenticated, dataSubscription, dataMe?.me?.id]);

  usePingEonCluster(!isAuthenticated);

  const isLoadingUserData = isAuthenticated && (!dataMe || !dataSubscription);

  // TV pauses splash on last frame until this gate clears
  useEffect(() => {
    if (!isSplashScreenEnabled || isLoadingUserData) return;
    window.dispatchEvent(new Event(APP_READY_EVENT));
  }, [isLoadingUserData]);

  const useMobileShell =
    !isTV && !isDesktop && !isMobileGameDetail && !showSetProfile;

  if (isLoadingUserData) {
    return <LoadingScreen />;
  }

  const content = showSetProfile ? <SetProfile /> : props.children;

  return (
    <div
      className={clsx(classes.mainLayout, {
        hideFooter:
          location.pathname === PageRoutes.Search ||
          showSetProfile ||
          isMobileGameDetail,
        noHeader: isMobileGameDetail || showSetProfile,
        fullHeight: isDesktop && location.pathname === PageRoutes.Home,
        mobileShell: useMobileShell,
      })}
      id="main-layout"
    >
      {renderHeader()}
      {!showSetProfile && <FooterControl />}
      {!isTV && !isDesktop && !isGameDetailPage && !showSetProfile && (
        <BottomTabBar />
      )}
      {useMobileShell ? (
        <div className={classes.contentScroller} id="content-scroll">
          {content}
        </div>
      ) : (
        content
      )}
      <ScrollRestoration />
    </div>
  );
};
